import React, { useEffect, useRef, useState } from "react";
import "./dashboard.css";
import Navbar from "../../components/navbar/navbar";
import Footer from "../../components/Footer/Footer";
import { useContext } from "react";
import { UserContext } from "../../context/UserContext";
import { useLocation, useNavigate } from "react-router-dom";
import { LoaderContext } from "../../context/LoaderContext";
import { API_URL } from "../../environmentVariables";
import Folder from "../../components/FolderComponent/Folder";
import File from "../../components/FileComponent/File";
import Loader from "../../components/loader/loader";
import SideNav from "../../components/SideNav/sideNav";
import {
  Alert,
  GetHash,
  GetLocalSetting,
  GetObjectToken,
  isMobileDevice,
  Log,
  MbToBytes,
  NormalizeBaseDirectory,
  SetLocalSetting,
  Try,
  tryParse,
  uuid,
} from "../../utils/genericUtils";

import { ChunkedWebsocketFileUpload, FormatFileName, GET, GetPendingUploads, POST, setAuth } from "../../utils/httpUtils";
import { SortFilesAlphabetically } from "../../utils/sortingFunctions";
import DirectoryComponent from "./directory-section";
import DashboardControls from "./dashboard-controls";
import UsageIndicator from "./dashboard-usage-indicator";
import { AlertContext } from "../../context/AlertContext";
import { NotificationsContext } from "../../context/NotificationsContext";
import { ConfirmationModal } from "../../components/Modals/ConfirmationModal/confirmation-modal";
import infoIcon from "../../assets/images/info.png";

const defaultProps = {
  dir: "",
  isTrash: false,
};

function Dashboard(props = defaultProps) {
  const { user, loginUser } = useContext(UserContext);
  const { show, showloader, hideloader } = useContext(LoaderContext);

  const { globalAlert } = useContext(AlertContext);
  const { pushNotification } = useContext(NotificationsContext);

  //refs
  const files = useRef([]);
  const folders = useRef([]);
  const dirChanged = useRef(true);
  const dir = useRef(props.dir ?? "/");
  const objectToken = useRef(undefined);
  const Elements = useRef([]);

  //state
  const [directory, setDirectory] = useState(null);
  const [uploadType, setUploadType] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [tempFile, setTempFile] = useState([]);
  const [indicator_hash, setHash] = useState("default");

  const [publicUserId, setPublicUserId] = useState(null);
  const [publicUser, setPublicUser] = useState(null);

  const [mobileTipModalActive, setMobileTipModalActive] = useState(false);

  const router = useNavigate();
  const location = useLocation();

  useEffect(() => {
    //get files and folders for this user
    //hide loader after all page load

    if (user != null) {
      getObjects()
        .then((data) => {
          Log("data: ", data);
          files.current = data.objects.filter((x) => x.objectType == "file");

          let show_hidden_filter = GetLocalSetting("show_hidden_objects");
          if (show_hidden_filter === false) files.current = files.current.filter((x) => x.hidden !== true);

          folders.current = data.objects.filter((x) => x.objectType == "folder");

          if (show_hidden_filter === false) folders.current = folders.current.filter((x) => x.hidden !== true);

          if (dirChanged.current == true) {
            FormatStorageObjects();
            setTempFile([]);
            checkForIncompleteObjectUploads();
            GetHash({ dir: dir.current, files: files.current, folders: folders.current }).then((hash) => {
              setHash(hash);
            });
          }
          hideloader();
        })
        .catch((err) => {
          Log("--> error: ", err);
          hideloader();
        });
    }

    if (isMobileDevice()) {
      let mobileTipOneSetting = GetLocalSetting("mobile-tip-1");
      let mobileTipOneSessionAcknowledged = GetLocalSetting("mobile-tip-1-session-acknowledged");
      if (mobileTipOneSessionAcknowledged !== true && (mobileTipOneSetting === undefined || mobileTipOneSetting === true))
        setMobileTipModalActive(true);
    }

    //try to get path variables
    let params = Try(
      () => location.pathname.split("/public/")[1],
      (e) => ""
    );
    let userId = Try(
      () => params.split("/")[0],
      (e) => null
    );
    let path = Try(
      () => params.split("/")[1],
      (e) => null
    );

    //get root for public view
    if (props.isPublicView && path && dir.current === "/" && (files.current.length === 0 || folders.current.length === 0)) {
      showloader();

      if (userId) setPublicUserId(userId);

      GET(`/public-object/${path}`).then((response) => {
        Log("response: ", response);
        hideloader();
        if (response.success === true) {
          let object = response.object;
          if (object.objectType === "file") {
            files.current = [object];
            //reset files
            folders.current = [];
          }
          if (object.objectType === "folder") {
            folders.current = [object];
            //reset folders
            files.current = [];
          }
          FormatStorageObjects();
          setTempFile([]);
          GetHash({ dir: dir.current, files: files.current, folders: folders.current }).then((hash) => {
            setHash(hash);
          });
        } else {
          router("/not-found");
        }
      });
    } else if (props.isPublicView) {
      //get child objects for public view
      showloader();

      if (userId) setPublicUserId(userId);

      getPublicObjects(userId).then((data) => {
        if (!data || !data.objects) {
          hideloader();
          setDir("/");
          return;
        }

        files.current = data.objects.filter((x) => x.objectType == "file");

        let show_hidden_filter = GetLocalSetting("show_hidden_objects");
        if (show_hidden_filter === false) files.current = files.current.filter((x) => x.hidden !== true);

        folders.current = data.objects.filter((x) => x.objectType == "folder");

        if (show_hidden_filter === false) folders.current = folders.current.filter((x) => x.hidden !== true);

        if (dirChanged.current == true) {
          FormatStorageObjects();
          setTempFile([]);
          GetHash({ dir: dir.current, files: files.current, folders: folders.current }).then((hash) => {
            setHash(hash);
          });
        }
        hideloader();
      });
    }
  }, [user, directory, forceUpdate, publicUser]);

  if (user == null && !props.isPublicView) {
    return;
  }

  function setDir(path) {
    dirChanged.current = true;
    dir.current = path;
    //clear memory of files in a directory
    //FunctionRevokeAllSessionUrls();
    setDirectory(path);
  }

  async function upload() {
    if (uploadType != null) {
      if (uploadType == "file") {
        await uploadFile();
      }

      if (uploadType == "folder") {
        uploadFolder();
      }
    }
  }

  async function getObjects() {
    try {
      let response = await GET(
        `/list${NormalizeBaseDirectory(dir.current)}`,
        user,
        objectToken.current ? [{ key: "x-object-token", value: objectToken.current }] : []
      );
      hideloader();
      if (response.success) {
        return response;
      } else {
        return response.error;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function getPublicObjects(userId = null) {
    let response = await GET(`/public-list/${userId ?? publicUserId}${NormalizeBaseDirectory(dir.current ?? "/")}`, user, [
      { key: "x-object-token", value: objectToken.current ?? "" },
    ]);
    hideloader();
    if (response.success) {
      return response;
    } else {
      return response.error;
    }
  }

  async function checkForIncompleteObjectUploads() {
    let get_pending_uploads_response = await GetPendingUploads(user, dir.current);
    Log("get_pending_uploads_response: ", get_pending_uploads_response);
    if (get_pending_uploads_response.success) {
      get_pending_uploads_response.uploadRequests.forEach((upload_request) => {
        if (upload_request.uploadWebsocketUrl) {
          let progress_id = "#psuedo-file-" + upload_request.id + "-psuedo-progress";

          let progressElement;
          //connect to the open websocket to retrieve progress updates
          const websocket = new WebSocket(upload_request.uploadWebsocketUrl);

          websocket.onopen = () => {
            //reset pseudo elements
            Log("creating psuedo file for: ", upload_request);
            createPsudoFile(undefined, undefined, get_pending_uploads_response.uploadRequests);
          };

          websocket.onerror = (e) => {
            Log("websocket error: ", upload_request, e);
          };

          websocket.onclose = () => {
            Log("websocket closed: ", upload_request);
          };

          //assign on message listener
          websocket.onmessage = (event) => {
            Log("ws on message!: ", event);
            let response = tryParse(event.data);
            response.totalBytes = upload_request.bytes;
            Log("target_id: ", progress_id);
            progressElement = document.querySelector(progress_id);
            Log("progress element: ", progressElement);
            if (progressElement) {
              HandleProgressEvent(progressElement, response);
            }
          };
        }
      });
    }
  }

  function createFolder() {
    return new Promise((resolve, reject) => {
      let request = new XMLHttpRequest();
      request.open("POST", API_URL + "/create-folder");
      setAuth(request, user);
      request.send(JSON.stringify({ directory: dir.current }));
      request.onload = () => {
        let response = JSON.parse(request.response);
        if (response.success) {
          dirChanged.current = true;
          setForceUpdate(!forceUpdate);
        } else {
          Log("response not success:", response);
        }
      };
      request.onerror = () => {};
    });
  }

  async function uploadFile() {
    setUploadType(null);
    let fileInput = document.querySelector("#hidden-file-upload");
    if (fileInput.files.length == 0) return;
    let file = fileInput.files[0];
    showloader();
    let request_upload_result = await RequestObjectUpload(file);
    if (request_upload_result.success) {
      //stream file uplaod to websocket here
      let upload_websocket = request_upload_result.upload_websocket;
      let progressElement;
      const psuedo_file_id = uuid();
      createPsudoFile(file.name, psuedo_file_id);
      await ChunkedWebsocketFileUpload(
        upload_websocket,
        request_upload_result.arrayBuffer,
        () => {
          showloader();
        },
        (response) => {
          let progress_id = "#psuedo-file-" + psuedo_file_id + "-psuedo-progress";
          progressElement = document.querySelector(progress_id);
          HandleProgressEvent(progressElement, response, response.totalBytes);
        },
        () => {
          //finished uploading file to server here
          fileInput.value = "";
          hideloader();
        },
        () => {
          //setForceUpdate(!forceUpdate);
        },
        (error) => {
          Alert(globalAlert, "error", "Error", error);
        },
        //1mb/s roughly
        MbToBytes(1)
      );
    } else {
      hideloader();
      if (request_upload_result.error) {
        Alert(globalAlert, "error", "Error", request_upload_result.error.message);
      }
    }
  }

  function RequestObjectUpload(file) {
    return new Promise(async (resolve) => {
      try {
        let fileReader = new FileReader();
        fileReader.onload = async (ev) => {
          //get array buffer after fileRead to determine file size
          let arrayBuffer = ev.target?.result;
          //determine number of chunks needed to uplaod whole file
          let fileBytes = arrayBuffer.byteLength;

          let response = await POST("/request-upload", undefined, user, [
            { key: "x-file-name", value: FormatFileName(file.name) },
            { key: "x-file-dir", value: dir.current },
            { key: "x-file-size", value: fileBytes },
          ]);

          return resolve({ ...response, arrayBuffer });
        };
        fileReader.readAsArrayBuffer(file);
      } catch (error) {
        return resolve({ success: false, error });
      }
    });
  }

  function HandleProgressEvent(progressElement, websocketResponse) {
    // Event listener for incoming messages from the server
    try {
      let progressResponse = websocketResponse;

      if (progressResponse.status && progressResponse.status === "failed") {
        Alert(globalAlert, "error", "File Failed To Upload", JSON.stringify(progressResponse));
      }

      //TO DO - document this
      if (Object.keys(progressResponse).includes("bytesUploaded")) {
        //update current bytes used
        user.bytesUsed = parseInt(user.bytesUsed) + progressResponse.bytesUploaded;
        if (progressResponse.bytesUploaded === 0) {
          setTempFile([]);
          setTimeout(() => {
            dirChanged.current = true; //set to true to reload
            setForceUpdate((prevState) => !prevState);
          }, 250);
        } else {
          return;
        }
      }

      //let file = progressResponse.file;
      let progress = progressResponse.progress;
      let progressPercentage = (progress / progressResponse.totalBytes).toFixed(2) * 100;

      if (progressPercentage >= 100 && progressResponse.totalBytes != 0) {
        setTempFile([]);
        setTimeout(() => {
          dirChanged.current = true; //set to true to reload
          setForceUpdate((prevState) => !prevState);
        }, 250);
      }

      // Update progress indicator here
      if (progressElement) progressElement.value = progressPercentage;
    } catch (error) {
      Log("error updating progress value:", error);
    }
  }

  function uploadFolder() {
    //set upload type back to null here after upload folder function call
    setUploadType(null);
  }

  function fowardFileUploadClick() {
    setUploadType("file");
    let fileUpload = document.querySelector("#hidden-file-upload");
    fileUpload.click();
  }

  function fowardFolderUploadClick() {
    setUploadType("folder");
    let fileUpload = document.querySelector("#hidden-folder-upload");
    fileUpload.click();
  }

  async function GetAllDirectories() {
    let res = await GET("/list-all-directories", user);
    if (res.success) {
      let dirs = Array.from(res.directories);
      dirs.sort((a, b) => {
        // Get full paths for comparison
        let pathA = a.baseDirectory + (a.objectName ? "/" + a.objectName : "");
        let pathB = b.baseDirectory + (b.objectName ? "/" + b.objectName : "");

        // First, compare by directory depth (number of slashes)
        let depthA = (pathA.match(/\//g) || []).length;
        let depthB = (pathB.match(/\//g) || []).length;

        if (depthA !== depthB) {
          return depthA - depthB; // Sort by depth
        }

        // If depths are equal, sort alphabetically
        return pathA.localeCompare(pathB);
      });

      return dirs;
    } else {
      return [{ objectName: "Could Not Get Dirs" }];
    }
  }

  /**
   * Uses the response from /list api to format all the objects into ui elements
   * consiting of files & folder components
   */
  async function FormatStorageObjects() {
    let elements = [];
    let possible_dirs = [];
    if (!props.isPublicView) possible_dirs = await GetAllDirectories();

    if (folders.current != null && folders.current.length != 0) {
      folders.current.sort((a, b) => a.objectName.localeCompare(b.objectName));
      folders.current.forEach((item) => {
        if (item.objectName !== "Trash") {
          let parent_dir = item.baseDirectory.split("/").pop();

          let grand_parent = item.baseDirectory.split(parent_dir)[0];

          if (grand_parent.charAt(grand_parent.length - 1) === "/") {
            grand_parent = grand_parent.substring(0, grand_parent.length - 1);
          }

          elements.push(
            <Folder
              key={item.id}
              isPublicView={props.isPublicView}
              possibleDirectories={possible_dirs}
              folderName={item.objectName}
              directory={item.baseDirectory}
              objectId={item.id}
              DateCreated={item.dateCreated}
              DateModified={item.dateModified}
              bytes={item.bytes}
              isTrashBin={props.isTrash}
              public={item.publicAccess}
              hidden={item.hidden}
              locked={item.locked}
              forceUpdate={() => {
                setTimeout(() => {
                  dirChanged.current = true; //set to true to reload
                  setForceUpdate((prevState) => !prevState);
                }, 500);
              }}
              userClick={(dir, token = undefined) => {
                objectToken.current = token;
                setDir(dir);
              }}
            />
          );
        }
      });
    }

    if (files.current != null && files.current.length != 0) {
      //Sort Mode
      SortFilesAlphabetically(files.current);
      Log("files current sort order", files.current);

      files.current.forEach((item) => {
        let parent_dir = item.baseDirectory.split("/").pop();

        let grand_parent = item.baseDirectory.split(parent_dir)[0];

        if (grand_parent.charAt(grand_parent.length - 1) === "/") {
          grand_parent = grand_parent.substring(0, grand_parent.length - 1);
        }

        elements.push(
          <File
            key={item.id}
            isPublicView={props.isPublicView}
            id={item.id}
            possibleDirectories={[...folders.current, { fullPath: grand_parent }]}
            fileName={item.objectName}
            directory={item.baseDirectory}
            objectId={item.id}
            objectUrl={item.objectUrl}
            size={item.bytes}
            DateCreated={item.dateCreated}
            DateModified={item.dateModified}
            bytes={item.bytes}
            isTrashBin={props.isTrash}
            public={item.publicAccess}
            hidden={item.hidden}
            locked={item.locked}
            forceUpdate={() => {
              setTimeout(() => {
                dirChanged.current = true; //set to true to reload
                setForceUpdate((prevState) => !prevState);
              }, 500);
            }}
          />
        );
      });
    }

    Elements.current = elements;
    dirChanged.current = false;
    if (!props.isPublicView) setForceUpdate(!forceUpdate);
  }

  function createPsudoFile(fileName, psuedoId, psuedoFiles = []) {
    if (psuedoFiles.length === 0 && fileName && psuedoId) {
      Log("creating psudo file A");
      if (files.current.length === 0) files.current = [{ name: "psuedo-file" }];
      let id = "psuedo-file-" + psuedoId;
      let psudoElement = <File isPsuedo={true} objectId={id} fileName={fileName} />;
      let arr = [...tempFile];
      arr.push(psudoElement);
      setTempFile(arr);
    } else {
      Log("creating psudo file B");
      let arr = [];
      psuedoFiles.forEach((object) => {
        if (object.uploadWebsocketUrl) {
          Log("object: ", object);
          let id = "psuedo-file-" + object.id;
          let psudoElement = <File isPsuedo={true} objectId={id} fileName={object.objectName} />;
          arr.push(psudoElement);
        }
      });
      setTempFile([...tempFile, ...arr]);
    }
  }

  function RenderIndicator() {
    if (!props.isPublicView) {
      return (
        <>
          <UsageIndicator stateHash={indicator_hash} />
          <div className="divider-line"></div>
        </>
      );
    }
  }

  function RenderControls() {
    if (!props.isPublicView) {
      return (
        <DashboardControls
          dir={dir.current}
          forwardFileUploadClick={fowardFileUploadClick}
          forwardFolderUploadClick={fowardFolderUploadClick}
          uploadFile={upload}
          createFolder={createFolder}
          forceRender={() => {
            setTimeout(() => {
              dirChanged.current = true; //set to true to reload
              setForceUpdate((prevState) => !prevState);
            }, 500);
          }}
        />
      );
    }
  }

  function RenderSideNav() {
    if (!props.isPublicView) {
      return <SideNav />;
    }
  }

  function RenderDirControls() {
    if (props.isPublicView) {
      let obj = files.current[0] ?? folders.current[0];
      if (!publicUser && obj && obj.ownerId) {
        let id = obj.ownerId;
        GET(`/user/${id}`).then((response) => {
          if (response.success && publicUser !== response.username) setPublicUser(response.username);
        });
      }
    }

    return (
      <>
        <DirectoryComponent user={user} dir={dir.current} setDir={setDir} isPublicView={props.isPublicView} ownerUserName={publicUser} />
      </>
    );
  }

  function RenderPublicViewHeader() {
    if (props.isPublicView) {
      return (
        <>
          <section className="usage-section">
            <h1 className="storage-usage-header">Viewing Public Files</h1>
          </section>
          <div className="divider-line"></div>
          <div className="public-view-spacer"></div>
        </>
      );
    }
  }

  return (
    <>
      <Loader />
      <Navbar active={props.isPublicView ? "" : "Dashboard"} />

      {/* Mobile Tip */}
      <ConfirmationModal
        id="mobile-tip-modal"
        icon={infoIcon}
        showDivider={true}
        isActive={mobileTipModalActive}
        header="Mobile Tip"
        message="We detected you are using a mobile device, to interact with files and folders please double tap!"
        includeDontAskAgain={true}
        confirmClass="btn-green"
        confirmText="Okay"
        dontAskSetting="mobile-tip-1"
        dontAskText="Dont show this tip again"
        useDeny={false}
        onConfirm={() => {
          SetLocalSetting("mobile-tip-1-session-acknowledged", true);
          setMobileTipModalActive(false);
        }}
      />

      <section className="user-dashboard">
        {/* Side Nav */}
        {RenderSideNav()}
        <section className="main-dashboard">
          {/* BYTES USAGE INIDCATOR */}
          {RenderIndicator()}

          {/* Dashboard Controls */}
          {RenderControls()}

          {/* Public View Header */}
          {RenderPublicViewHeader()}

          {/* Dir Section */}
          {RenderDirControls()}

          <section
            id="files"
            className="files-section"
            style={
              files.current != null && folders.current != null && files.current.length < 1 && folders.current.length < 1
                ? { justifyContent: "center" }
                : {}
            }
          >
            {tempFile.length === 0 && files.current != null && folders.current != null && files.current.length < 1 && folders.current.length < 1 ? (
              <h5 className="empty-files-text">It's empty in here...</h5>
            ) : (
              <>{Elements.current}</>
            )}
            {tempFile}
          </section>
        </section>
        {/*Folders and Files Section*/}
        {/*Visual View*/} {/*List View*/}
        {/*File and Folder Components  --edit --delete --rename --download --share */}
      </section>
      <Footer />
    </>
  );
}

export default Dashboard;
